import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';
import { Transaction } from '../types';
import api from '../services/api';

interface SettlementManagementProps {
  onTransactionUpdate: () => void;
}

const SettlementManagement: React.FC<SettlementManagementProps> = ({ onTransactionUpdate }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();
  const [settlementTargets, setSettlementTargets] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [processingBatch, setProcessingBatch] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const fetchSettlementTargets = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/settlements/targets');
      const data = response.data;
      setSettlementTargets(data);
      setSelectedItems(new Set());
    } catch (error) {
      console.error(t('settlement:fetchError'), error);
      alert(t('settlement:fetchDataError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const toggleSelectItem = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === settlementTargets.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(settlementTargets.map(t => t.id)));
    }
  };

  const getSelectedTotal = () => {
    return settlementTargets
      .filter(t => selectedItems.has(t.id))
      .reduce((sum, t) => sum + (t.type === 'income' ? t.amount : -t.amount), 0);
  };

  const processBatchSettlement = async (createIncomeData: boolean) => {
    if (selectedItems.size === 0) {
      alert(t('settlement:selectItemsToSettle'));
      return;
    }

    const message = createIncomeData 
      ? `${t('settlement:batchSettlementConfirm')} (${selectedItems.size}${t('common:count')}, ${t('common:income')})`
      : `${t('settlement:batchSettlementConfirm')} (${selectedItems.size}${t('common:count')}, ${t('settlement:noIncome')})`;
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      setProcessingBatch(true);
      const response = await api.post('/settlements/batch-process', {
        transactionIds: Array.from(selectedItems),
        createIncomeData,
      });

      const result = response.data;
      alert(result.message);
      await fetchSettlementTargets();
      onTransactionUpdate();
    } catch (error) {
      console.error('一括精算処理エラー:', error);
      alert(error instanceof Error ? error.message : t('settlement:batchSettlementFailed'));
    } finally {
      setProcessingBatch(false);
    }
  };

  const cancelBatchSettlement = async () => {
    if (selectedItems.size === 0) {
      alert(t('settlement:selectItemsToCancel'));
      return;
    }

    if (!window.confirm(`${t('settlement:batchCancelConfirm')} (${selectedItems.size}${t('common:count')})`)) {
      return;
    }

    try {
      setProcessingBatch(true);
      const response = await api.post('/settlements/batch-cancel', {
        transactionIds: Array.from(selectedItems),
      });

      const result = response.data;
      alert(result.message);
      await fetchSettlementTargets();
      onTransactionUpdate();
    } catch (error) {
      console.error('一括キャンセル処理エラー:', error);
      alert(error instanceof Error ? error.message : t('settlement:batchCancelFailed'));
    } finally {
      setProcessingBatch(false);
    }
  };

  const processSettlement = async (transactionId: string, createIncomeData: boolean) => {
    try {
      setProcessing(transactionId);
      const response = await api.post('/settlements/process', {
        transactionId,
        createIncomeData,
      });

      const result = response.data;
      alert(result.message);
      await fetchSettlementTargets();
      onTransactionUpdate();
    } catch (error) {
      console.error('精算処理エラー:', error);
      alert(error instanceof Error ? error.message : t('settlement:settlementFailed'));
    } finally {
      setProcessing(null);
    }
  };

  const cancelSettlement = async (transactionId: string) => {
    if (!window.confirm(t('settlement:cancelConfirm'))) {
      return;
    }

    try {
      setProcessing(transactionId);
      const response = await api.post('/settlements/cancel', {
        transactionId,
      });

      const result = response.data;
      alert(result.message);
      await fetchSettlementTargets();
      onTransactionUpdate();
    } catch (error) {
      console.error('精算キャンセルエラー:', error);
      alert(error instanceof Error ? error.message : t('settlement:cancelFailed'));
    } finally {
      setProcessing(null);
    }
  };

  useEffect(() => {
    fetchSettlementTargets();
    
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, [fetchSettlementTargets]);

  if (loading) {
    return <div className="text-center py-4">{t('common:loading')}</div>;
  }

  return (
    <div className="settlement-management">
      <h2>{t('settlement:settlementManagement')}</h2>
      
      <button
        onClick={fetchSettlementTargets}
        disabled={loading}
        className="btn btn-primary"
      >
        {t('common:refresh')}
      </button>

      {settlementTargets.length === 0 ? (
        <div className="empty-state">
          <p>{t('settlement:noTargetData')}</p>
        </div>
      ) : (
        <>
          <div className="settlement-controls">
            <div className={`settlement-selection-info ${isMobile ? 'mobile' : ''}`}>
              <span>
                {t('common:selected')}: {selectedItems.size}{t('common:count')}
              </span>
              {selectedItems.size > 0 && (
                <span>
                  {t('common:total')}: {formatCurrency(Math.abs(getSelectedTotal()))}
                </span>
              )}
            </div>
            <div className={`settlement-buttons ${isMobile ? 'mobile' : ''}`} style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => processBatchSettlement(true)}
                disabled={selectedItems.size === 0 || processingBatch}
                className="btn btn-success"
                style={{
                  flex: 1,
                  padding: isMobile ? '8px 12px' : '6px 8px',
                  fontSize: isMobile ? '12px' : '11px',
                  backgroundColor: '#10b981',
                  borderColor: '#10b981',
                  color: 'white',
                  whiteSpace: 'nowrap'
                }}
              >
                {processingBatch ? (isMobile ? '⏳ 処理中' : '⏳ 処理中') : (isMobile ? '💰 精算(収入)' : '💰 精算(収入)')}
              </button>
              <button
                onClick={() => processBatchSettlement(false)}
                disabled={selectedItems.size === 0 || processingBatch}
                className="btn btn-primary"
                style={{
                  flex: 1,
                  padding: isMobile ? '8px 12px' : '6px 8px',
                  fontSize: isMobile ? '12px' : '11px',
                  whiteSpace: 'nowrap'
                }}
              >
                {processingBatch ? (isMobile ? '⏳ 処理中' : '⏳ 処理中') : (isMobile ? '✅ 精算' : '✅ 精算')}
              </button>
              <button
                onClick={cancelBatchSettlement}
                disabled={selectedItems.size === 0 || processingBatch}
                className="btn btn-secondary"
                style={{
                  flex: 1,
                  padding: isMobile ? '8px 12px' : '6px 8px',
                  fontSize: isMobile ? '12px' : '11px',
                  whiteSpace: 'nowrap'
                }}
              >
                {processingBatch ? (isMobile ? '⏳ 処理中' : '⏳ 処理中') : (isMobile ? '❌ キャンセル' : '❌ キャンセル')}
              </button>
            </div>
          </div>

          {isMobile ? (
            <div className="settlement-mobile-container">
              <div className="settlement-select-all" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '8px' }}>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedItems.size === settlementTargets.length && settlementTargets.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium">すべて選択</span>
                </label>
              </div>
              {selectedItems.size > 0 && (
                <div className="settlement-batch-action" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#e0f2fe', borderRadius: '8px', border: '1px solid #0891b2' }}>
                  <div className="flex items-center gap-2" style={{ marginBottom: '8px' }}>
                    <span className="text-sm font-bold">✔️ 精算する（{selectedItems.size}件、合計金額：{formatCurrency(Math.abs(getSelectedTotal()))}）</span>
                  </div>
                </div>
              )}
              {settlementTargets.map((transaction) => (
                <div key={transaction.id} className="settlement-card" style={{
                  backgroundColor: selectedItems.has(transaction.id) ? '#f0f9ff' : '#ffffff',
                  border: selectedItems.has(transaction.id) ? '2px solid #0891b2' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '12px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <div className="settlement-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(transaction.id)}
                        onChange={() => toggleSelectItem(transaction.id)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-600">
                        {new Date(transaction.date).toLocaleDateString('ja-JP', { 
                          year: 'numeric', 
                          month: '2-digit', 
                          day: '2-digit',
                          weekday: 'short' 
                        })}
                      </span>
                    </label>
                  </div>
                  
                  <div className="settlement-card-content">
                    <div className="settlement-card-main" style={{ marginBottom: '8px' }}>
                      <div className="flex items-center gap-2" style={{ marginBottom: '4px' }}>
                        <span style={{ fontSize: '16px' }}>
                          {transaction.category === 'タクシー代' ? '🚗' : 
                           transaction.category === '昼食代' ? '🍽️' : 
                           transaction.category === '夕食代' ? '🍽️' : 
                           transaction.category === '朝食代' ? '🍽️' : 
                           transaction.category === '交通費' ? '🚌' : 
                           transaction.category === '会議費' ? '🤝' : 
                           transaction.category === '接待費' ? '🍷' : 
                           transaction.category === '宿泊費' ? '🏨' : 
                           transaction.category === '通信費' ? '📱' : 
                           transaction.category === '文具・消耗品' ? '📝' : 
                           transaction.category === '書籍・研修' ? '📚' : '💼'}
                        </span>
                        <span className="text-base font-medium text-gray-900">
                          {transaction.category}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" style={{ marginBottom: '4px' }}>
                        <span style={{ fontSize: '16px' }}>💰</span>
                        <span className={`text-lg font-bold ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(transaction.amount)}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span style={{ fontSize: '16px' }}>📝</span>
                        <span className="text-sm text-gray-700">
                          {transaction.description || '説明なし'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="settlement-card-actions" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button
                        onClick={() => processSettlement(transaction.id, true)}
                        disabled={processing === transaction.id}
                        className="btn btn-success"
                        style={{ 
                          flex: 1,
                          padding: '8px 12px', 
                          fontSize: '12px',
                          backgroundColor: '#10b981',
                          borderColor: '#10b981',
                          color: 'white',
                          opacity: processing === transaction.id ? 0.5 : 1,
                          cursor: processing === transaction.id ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {processing === transaction.id ? '⏳ 処理中' : '💰 精算(収入)'}
                      </button>
                      <button
                        onClick={() => processSettlement(transaction.id, false)}
                        disabled={processing === transaction.id}
                        className="btn btn-primary"
                        style={{ 
                          flex: 1,
                          padding: '8px 12px', 
                          fontSize: '12px',
                          opacity: processing === transaction.id ? 0.5 : 1,
                          cursor: processing === transaction.id ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {processing === transaction.id ? '⏳ 処理中' : '✅ 精算'}
                      </button>
                      <button
                        onClick={() => cancelSettlement(transaction.id)}
                        disabled={processing === transaction.id}
                        className="btn btn-secondary"
                        style={{ 
                          flex: 1,
                          padding: '8px 12px', 
                          fontSize: '12px',
                          opacity: processing === transaction.id ? 0.5 : 1,
                          cursor: processing === transaction.id ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {processing === transaction.id ? '⏳ 処理中' : '❌ キャンセル'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="budget-table-container" style={{ minWidth: '1200px' }}>
              <table className="budget-table">
                <thead>
                  <tr>
                    <th className="budget-table-header" style={{ width: '80px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedItems.size === settlementTargets.length && settlementTargets.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </th>
                    <th className="budget-table-header" style={{ width: '140px' }}>
                      📅 {t('common:date')}
                    </th>
                    <th className="budget-table-header" style={{ width: '120px' }}>
                      🏷️ {t('common:type')}
                    </th>
                    <th className="budget-table-header" style={{ width: '150px' }}>
                      📂 {t('common:category')}
                    </th>
                    <th className="budget-table-header" style={{ width: '140px' }}>
                      💰 {t('common:amount')}
                    </th>
                    <th className="budget-table-header" style={{ width: '280px' }}>
                      📝 {t('common:description')}
                    </th>
                    <th className="budget-table-header" style={{ width: '270px' }}>
                      ⚡ {t('settlement:actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {settlementTargets.map((transaction) => (
                    <tr 
                      key={transaction.id} 
                      className={`budget-table-row ${selectedItems.has(transaction.id) ? 'selected-row' : ''}`}
                    >
                      <td className="budget-table-cell" style={{ width: '80px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedItems.has(transaction.id)}
                          onChange={() => toggleSelectItem(transaction.id)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="budget-table-cell" style={{ width: '140px' }}>
                        <div className="text-sm font-medium text-gray-900">
                          {new Date(transaction.date).toLocaleDateString('ja-JP')}
                        </div>
                      </td>
                      <td className="budget-table-cell" style={{ width: '120px' }}>
                        <span
                          className={`budget-category-type ${transaction.type}`}
                        >
                          {transaction.type === 'income' ? t('common:income') : t('common:expense')}
                        </span>
                      </td>
                      <td className="budget-table-cell" style={{ width: '150px' }}>
                        <div className="budget-category-name">
                          {transaction.category}
                        </div>
                      </td>
                      <td className="budget-table-cell" style={{ width: '140px' }}>
                        <div className={`text-sm font-bold ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                          {transaction.type === 'income' ? '+' : '-'}
                          {formatCurrency(transaction.amount)}
                        </div>
                      </td>
                      <td className="budget-table-cell" style={{ width: '280px' }}>
                        <div className="text-sm text-gray-700 truncate" title={transaction.description || ''}>
                          {transaction.description || '📝 説明なし'}
                        </div>
                      </td>
                      <td className="budget-table-cell" style={{ width: '270px', textAlign: 'center' }}>
                        <div className="flex gap-1 justify-center flex-wrap">
                          <button
                            onClick={() => processSettlement(transaction.id, true)}
                            disabled={processing === transaction.id}
                            className="btn btn-success"
                            style={{ 
                              padding: '6px 8px', 
                              fontSize: '11px',
                              backgroundColor: '#10b981',
                              borderColor: '#10b981',
                              color: 'white',
                              opacity: processing === transaction.id ? 0.5 : 1,
                              cursor: processing === transaction.id ? 'not-allowed' : 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {processing === transaction.id ? '⏳ 処理中' : '💰 精算(収入)'}
                          </button>
                          <button
                            onClick={() => processSettlement(transaction.id, false)}
                            disabled={processing === transaction.id}
                            className="btn btn-primary"
                            style={{ 
                              padding: '6px 8px', 
                              fontSize: '11px',
                              opacity: processing === transaction.id ? 0.5 : 1,
                              cursor: processing === transaction.id ? 'not-allowed' : 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {processing === transaction.id ? '⏳ 処理中' : '✅ 精算'}
                          </button>
                          <button
                            onClick={() => cancelSettlement(transaction.id)}
                            disabled={processing === transaction.id}
                            className="btn btn-secondary"
                            style={{ 
                              padding: '6px 8px', 
                              fontSize: '11px',
                              opacity: processing === transaction.id ? 0.5 : 1,
                              cursor: processing === transaction.id ? 'not-allowed' : 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {processing === transaction.id ? '⏳ 処理中' : '❌ キャンセル'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </>
        )}
      </div>
  );
};

export default SettlementManagement;